import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import UpperCaseInput from '../../../components/UpperCaseInput';
import { Label } from '../../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../components/ui/tooltip';
import { Plus, Pencil, Trash2, Box, Upload, Users, Search, AlertTriangle, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '../../../App';
import { API_URL, INITIAL_TRAILER_FORM, getExpiryStatus, formatDateDDMMYYYY } from '../utils/constants';

const TrailersTab = ({ trailers, companies, onRefresh, onBulkUpload, onAssign, getCompanyName, isTransportista }) => {
  const { user: currentUser } = useAuth();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedTrailer, setSelectedTrailer] = useState(null);
  const [formData, setFormData] = useState(INITIAL_TRAILER_FORM);
  const [justification, setJustification] = useState('');
  
  // Filtros
  const [filterCompany, setFilterCompany] = useState('');
  const [filterPlate, setFilterPlate] = useState('');
  
  // Filtrar acoplados
  const filteredTrailers = useMemo(() => {
    return trailers.filter(t => {
      if (filterCompany && t.company_id !== filterCompany) return false;
      if (filterPlate && !t.license_plate?.toLowerCase().includes(filterPlate.toLowerCase().replace(/\s/g, ''))) return false;
      return true;
    });
  }, [trailers, filterCompany, filterPlate]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      // Eliminar espacios de la patente
      const dataToSend = { ...formData, license_plate: formData.license_plate.replace(/\s/g, '') };
      await axios.post(`${API_URL}/trailers`, dataToSend);
      toast.success('Acoplado creado exitosamente');
      setIsCreateModalOpen(false);
      resetForm();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear acoplado');
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!justification.trim()) {
      toast.error('La justificación es obligatoria');
      return;
    }
    try {
      // Eliminar espacios de la patente
      const dataToSend = { ...formData, license_plate: formData.license_plate.replace(/\s/g, '') };
      await axios.put(`${API_URL}/trailers/${selectedTrailer.id}`, { justification, data: dataToSend });
      toast.success('Acoplado actualizado exitosamente');
      setIsEditModalOpen(false);
      resetForm();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar acoplado');
    }
  };

  const handleDelete = async (trailerId) => {
    if (!window.confirm('¿Estás seguro de eliminar este acoplado?')) return;
    try {
      await axios.delete(`${API_URL}/trailers/${trailerId}`);
      toast.success('Acoplado eliminado');
      onRefresh();
    } catch (error) {
      toast.error('Error al eliminar acoplado');
    }
  };

  const resetForm = () => {
    setFormData(INITIAL_TRAILER_FORM);
    setJustification('');
    setSelectedTrailer(null);
  };

  const openEditModal = (trailer) => {
    setSelectedTrailer(trailer);
    setFormData({
      license_plate: trailer.license_plate, brand: trailer.brand,
      international_insurance: trailer.international_insurance, insurance_expiry: trailer.insurance_expiry,
      seal_quantity: trailer.seal_quantity, company_id: trailer.company_id
    });
    setIsEditModalOpen(true);
  };

  return (
    <div className="space-y-4" data-testid="trailers-tab">
      <div className="flex items-center justify-between">
        <p className="text-slate-600">Gestión de acoplados</p>
        <div className="flex gap-2">
          {currentUser.role === 'MASTER' && (
            <>
              <Button onClick={() => onBulkUpload('trailers')} variant="outline" size="sm" className="text-emerald-600 border-emerald-300 hover:bg-emerald-50">
                <Upload size={16} className="mr-1" /> Carga Masiva
              </Button>
              <Button onClick={() => onAssign('trailers')} variant="outline" size="sm" className="text-violet-600 border-violet-300 hover:bg-violet-50">
                <Users size={16} className="mr-1" /> Asignar Admin
              </Button>
            </>
          )}
          <Button onClick={() => setIsCreateModalOpen(true)} className="bg-primary hover:bg-primary-hover text-white rounded-sm">
            <Plus className="mr-2" size={18} />Crear Acoplado
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Search size={16} className="text-slate-400" />
            <span className="text-sm text-slate-600">Filtrar:</span>
          </div>
          <Select value={filterCompany || "all"} onValueChange={(v) => setFilterCompany(v === "all" ? "" : v)}>
            <SelectTrigger className="w-48 h-8 text-sm">
              <SelectValue placeholder="Todas las empresas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las empresas</SelectItem>
              {companies.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
            </SelectContent>
          </Select>
          <Input
            placeholder="Buscar por patente..."
            value={filterPlate}
            onChange={(e) => setFilterPlate(e.target.value)}
            className="h-8 text-sm w-40"
          />
          {(filterCompany || filterPlate) && (
            <Button variant="ghost" size="sm" onClick={() => { setFilterCompany(''); setFilterPlate(''); }} className="text-slate-500 h-8">
              Limpiar
            </Button>
          )}
          <span className="text-xs text-slate-500">{filteredTrailers.length} de {trailers.length}</span>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Patente</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Marca</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Seguro Int.</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Vencimiento</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Precintos</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Empresa</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            <TooltipProvider>
              {filteredTrailers.map((trailer) => {
                const expiryStatus = getExpiryStatus(trailer.insurance_expiry);
                return (
                  <tr key={trailer.id} className={`hover:bg-slate-50 transition-colors ${expiryStatus.status === 'expired' ? 'bg-red-50' : expiryStatus.status === 'warning' ? 'bg-amber-50' : ''}`}>
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-2">
                        <Box size={16} className="text-slate-400" />
                        <span className="text-sm font-mono font-bold text-slate-900">{trailer.license_plate}</span>
                      </div>
                    </td>
                    <td className="px-6 py-3 text-sm text-slate-900">{trailer.brand}</td>
                    <td className="px-6 py-3 text-sm font-mono text-slate-600">{trailer.international_insurance}</td>
                    <td className="px-6 py-3">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className={`flex items-center gap-1.5 text-sm font-mono ${
                            expiryStatus.status === 'expired' ? 'text-red-600 font-semibold' : 
                            expiryStatus.status === 'warning' ? 'text-amber-600 font-semibold' : 'text-slate-600'
                          }`}>
                            {expiryStatus.status === 'expired' ? <AlertCircle size={14} className="text-red-500" /> :
                             expiryStatus.status === 'warning' ? <AlertTriangle size={14} className="text-amber-500" /> :
                             <CheckCircle size={14} className="text-emerald-500" />}
                            {formatDateDDMMYYYY(trailer.insurance_expiry)}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{expiryStatus.label}</p>
                        </TooltipContent>
                      </Tooltip>
                    </td>
                    <td className="px-6 py-3 text-sm font-mono text-slate-900">{trailer.seal_quantity}</td>
                    <td className="px-6 py-3 text-sm text-slate-600">{getCompanyName(trailer.company_id)}</td>
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-2">
                        <Button onClick={() => openEditModal(trailer)} variant="ghost" size="sm" className="text-blue-600 hover:bg-blue-50"><Pencil size={16} /></Button>
                        <Button onClick={() => handleDelete(trailer.id)} variant="ghost" size="sm" className="text-red-600 hover:bg-red-50"><Trash2 size={16} /></Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </TooltipProvider>
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Crear Acoplado</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2"><Label>Patente *</Label><UpperCaseInput value={formData.license_plate} onChange={(e) => setFormData({ ...formData, license_plate: e.target.value })} required /></div>
            <div className="space-y-2"><Label>Marca *</Label><UpperCaseInput value={formData.brand} onChange={(e) => setFormData({ ...formData, brand: e.target.value })} required /></div>
            <div className="space-y-2"><Label>Seguro Internacional *</Label><UpperCaseInput value={formData.international_insurance} onChange={(e) => setFormData({ ...formData, international_insurance: e.target.value })} required /></div>
            <div className="space-y-2"><Label>Vencimiento Seguro *</Label><Input type="date" value={formData.insurance_expiry} onChange={(e) => setFormData({ ...formData, insurance_expiry: e.target.value })} required /></div>
            <div className="space-y-2"><Label>Cantidad Precintos *</Label><Input type="number" value={formData.seal_quantity} onChange={(e) => setFormData({ ...formData, seal_quantity: parseInt(e.target.value) })} required /></div>
            <div className="space-y-2">
              <Label>Empresa *</Label>
              <Select value={formData.company_id} onValueChange={(value) => setFormData({ ...formData, company_id: value })}>
                <SelectTrigger><SelectValue placeholder="Seleccionar empresa" /></SelectTrigger>
                <SelectContent>{companies.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full bg-primary hover:bg-primary-hover text-white">Crear Acoplado</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Editar Acoplado</DialogTitle></DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4">
            <div className="space-y-2"><Label>Patente</Label><UpperCaseInput value={formData.license_plate} onChange={(e) => setFormData({ ...formData, license_plate: e.target.value })} /></div>
            <div className="space-y-2"><Label>Marca</Label><UpperCaseInput value={formData.brand} onChange={(e) => setFormData({ ...formData, brand: e.target.value })} /></div>
            <div className="space-y-2"><Label>Seguro Internacional</Label><UpperCaseInput value={formData.international_insurance} onChange={(e) => setFormData({ ...formData, international_insurance: e.target.value })} /></div>
            <div className="space-y-2"><Label>Vencimiento Seguro</Label><Input type="date" value={formData.insurance_expiry} onChange={(e) => setFormData({ ...formData, insurance_expiry: e.target.value })} /></div>
            <div className="space-y-2"><Label>Cantidad Precintos</Label><Input type="number" value={formData.seal_quantity} onChange={(e) => setFormData({ ...formData, seal_quantity: parseInt(e.target.value) })} /></div>
            <div className="space-y-2">
              <Label>Empresa Transportista</Label>
              <Select value={formData.company_id} onValueChange={(value) => setFormData({ ...formData, company_id: value })} data-testid="edit-trailer-company-select">
                <SelectTrigger className="w-full"><SelectValue placeholder="Seleccionar empresa" /></SelectTrigger>
                <SelectContent>{companies.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-red-600">Justificación (obligatoria) *</Label>
              <UpperCaseInput value={justification} onChange={(e) => setJustification(e.target.value)} required placeholder="Motivo de la edición" />
            </div>
            <Button type="submit" className="w-full bg-primary hover:bg-primary-hover text-white">Actualizar Acoplado</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TrailersTab;
